import { pathEq, pathOr, sortBy } from 'ramda';
import matchSorter from 'match-sorter';

import { USStatesDropDownOptions } from 'helpers/USMapStates';
import { Filters } from 'scenes/Home/scenes/MerchantVenuesOnboarding/components/PlacesFilters/PlacesFilters.queries';

type GeoCode = {
  latitude: number;
  longitude: number;
};

type GeoViewPort = GeoCode & {
  width: number | string;
  height: number | string;
  zoom: number;
};

type PlaceCategory = {
  code: string;
  description: string | null;
  type: string;
};

type Place = {
  name: string;
  dataSource: string;
  externalId: string;
  uuid?: string;

  address: {
    street: string;
    suite?: string;
    city: string;
    state: string;
    postal: string;
    country: string;
  };
  geocode: GeoCode;
  categoriesV2?: PlaceCategory[];
};

type PlacesServiceResponse = {
  getPlaces: Place[];
};

type PlacesServiceParams = {
  merchantUuid: string;
  name: string;
  limit: number;
  geocodeFilter?: GeoCode;
  categoriesFilter?: string[];
  offset?: number;
  filters?: {
    state?: string;
    city?: string;
  };
};

const DEFAULT_GEO_CODE = {
  latitude: 40,
  longitude: -96,
};

const DEFAULT_ZOOM = 3;

const DEFAULT_GEO_VIEWPORT = {
  width: '100%',
  height: 650,
  ...DEFAULT_GEO_CODE,
  zoom: DEFAULT_ZOOM,
};

const MIN_NUMBER_OF_PLACES_TO_SHOW_FILTERS = 12;
const MIN_VENUE_LIMIT = 5;
const VENUE_LIMIT = 15;

const sortPlacesByState = sortBy(pathOr('', ['address', 'state']));

const getUSStateDropdownOptions = (allPlaces: Place[]) =>
  USStatesDropDownOptions.filter((option) =>
    allPlaces.some((place) => place.address.state === option.value),
  );

// scale by 20% or a min of 5
const getMinLimit = (numPlaces: number) =>
  Math.max(Math.ceil(numPlaces * 1.2), MIN_VENUE_LIMIT);

const applyFiltersToPlaces = (
  places: Place[],
  { placesStateFilter, placesNameFilter, placesCityFilter }: Filters,
): Place[] => {
  if (!places.length) return places;

  let filteredResults = [...places];

  // filter by state
  filteredResults = placesStateFilter
    ? filteredResults.filter((place) =>
        pathEq(['address', 'state'], placesStateFilter)(place),
      )
    : filteredResults;

  // filter by name
  filteredResults = placesNameFilter
    ? matchSorter(filteredResults, placesNameFilter, {
        keys: ['name'],
      })
    : filteredResults;

  // filter by city
  filteredResults = placesCityFilter
    ? matchSorter(filteredResults, placesCityFilter, {
        keys: ['address.city'],
      })
    : filteredResults;

  return filteredResults;
};

export {
  MIN_NUMBER_OF_PLACES_TO_SHOW_FILTERS,
  MIN_VENUE_LIMIT,
  VENUE_LIMIT,
  DEFAULT_GEO_CODE,
  DEFAULT_GEO_VIEWPORT,
  Place,
  PlacesServiceParams,
  PlacesServiceResponse,
  GeoCode,
  GeoViewPort,
  sortPlacesByState,
  getUSStateDropdownOptions,
  getMinLimit,
  applyFiltersToPlaces,
};
